// Fase 6 end-of-phase item 2/3 (2026-09-06): frontend support for the
// backend's Idempotency-Key mechanism (core/ai/idempotency.js, AF-04/06/27).
// Three routes (/api/bob-chat, /api/project-setup, /api/gpt) claim a key
// before doing any real LLM/DB work; a request that arrives with the SAME
// key as one already completed gets that same result replayed instead of
// re-executing (protects against double-submit, a dropped connection the
// user retries manually, or two tabs/devices racing the same action).
//
// Until now no frontend call site sent this header, so the backend fell
// back to hashing the request body -- correct for "same body -> same
// operation" but capable of a false collision: two GENUINELY DIFFERENT
// user actions that happen to produce an identical body (e.g. the first
// message of two separate fresh conversations both being "hi", or the
// same scope document intentionally submitted twice to create two
// projects) would incorrectly replay each other's result within the TTL
// window. Generating the key HERE, client-side, per logical user action
// (not per network attempt) fixes that: the key is now tied to "this one
// thing the user asked for," not to what the body happens to contain, so
// content that legitimately repeats across distinct actions no longer
// collides. The body-hash fallback in core/ai/idempotency.js stays in
// place for callers that don't send this header (WhatsApp, any future
// non-browser client), which is a materially smaller collision surface
// than "every request KrewUp's own frontend makes."
//
// Rule this generator exists to satisfy: SAME logical action, retried ->
// SAME key (see withIdempotentRetry below, the one place today that
// actually retries). NEW logical action -> NEW key (a fresh
// crypto.randomUUID() every time the caller asks for one, i.e. once per
// user-initiated send/submit -- never reused across two different sends).
export function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * fetch() wrapper for the 3 idempotent AI routes: attaches a fresh
 * Idempotency-Key (unless the caller already put one in `init.headers`)
 * and, on a NETWORK-level failure only (the fetch itself throwing --
 * DNS/connection drop/timeout, never a non-2xx HTTP response), retries
 * ONCE after a short delay, reusing the SAME key. That single retry is
 * exactly the scenario Idempotency-Key exists for: the first attempt may
 * have actually reached the server and completed server-side even though
 * the client never saw the response -- reusing the key means the retry
 * either safely re-executes (if the first attempt never got that far) or
 * gets the first attempt's real result replayed back (if it did), never a
 * duplicate mutation/charge either way.
 *
 * Does NOT retry on a non-network error (4xx/5xx) -- those are real
 * outcomes (validation error, insufficient credits, a genuine 500) the
 * caller should handle/surface, not blindly resubmit. Also does NOT retry
 * when `init.signal` caused the failure (AF-33, 2026-09-07): a caller
 * passing an AbortSignal (e.g. a "Cancel" button, or cleanup on unmount)
 * means the request was deliberately abandoned -- resubmitting it anyway
 * would defeat the whole point of cancelling, and could still cost real
 * credits/tokens for a request nobody wants anymore.
 *
 * Not used for /api/project-setup's upload (see ProjectSetupModal.tsx) --
 * that call streams an SSE response the client partially consumes as it
 * arrives, so a network drop mid-stream can't be safely retried by just
 * re-sending the same request; the key is still generated and sent there
 * (protects against a manual re-upload/multi-tab race), just without this
 * wrapper's automatic single retry.
 */
export async function withIdempotentRetry(
  url: string,
  init: RequestInit,
  { retries = 1, retryDelayMs = 500 }: { retries?: number; retryDelayMs?: number } = {}
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Idempotency-Key')) {
    headers.set('Idempotency-Key', generateIdempotencyKey());
  }
  const initWithKey: RequestInit = { ...init, headers };

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(url, initWithKey);
    } catch (err) {
      lastError = err;
      const isAbort = init.signal?.aborted || (err instanceof DOMException && err.name === 'AbortError');
      if (isAbort) throw err; // deliberate cancellation -- never retry
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      }
    }
  }
  throw lastError;
}
